import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ErrorDataSummaryModal from 'components/common/ErrorDataSummaryModal/ErrorDataSummaryModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { CompanyNode, CounterPattern, DepartmentNode } from 'features/common/models';
import { SaleType, SaleTypeMap } from 'features/common/models/saletype';
import organizationTreeService from 'features/common/organizationTreeService';
import _ from 'lodash';
import moment from 'moment';
import { FormEvent, Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Row, Table } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import {
  DATE_FORMAT_YYYY_MM_DD,
  FEATURE_STORE_ID,
  displayError,
  findParentKeys,
  handleServiceError,
  isCompanyNode,
} from 'utils';
import { CONSISTENCY_CHECK_SOURCE, NG_NUMBER_CHECK_SOURCE } from '../constants';
import { Restaurant, StoreSettingRequestPayload, UpdateStoreSettingRequestPayload } from '../models';
import storeSettingService from '../storeSettingService';
import { getBackgroundColorByClassification, getPaddingByClassification } from '../utils';
import './StoreSettingPage.scss';

interface FormSearchValueProps {
  dataDate: Date;
  storeName: string;
  saleTypeId: string;
  ngNumberType: number;
  isConsistencyType: number;
}

type StoreListFormValues = {
  restaurants: Restaurant[];
};

const CustomInputDatePicker = forwardRef((props: any, ref: any) => {
  // You can style this further
  return (
    <>
      <div style={{ position: 'relative' }}>
        <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
        <input
          className="form-control text-center"
          onClick={props.onClick}
          style={{ paddingLeft: '32px' }}
          value={props.value}
          onChange={props.onChange}
          type="text"
          readOnly={props.readonly}
        />
      </div>
    </>
  );
});

function StoreSettingPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //ref
  const formSearchRef = useRef<HTMLFormElement>(null);
  const currentRestaurantIndex = useRef<number>(0);
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnRevalidateRef = useRef<HTMLButtonElement>(null);
  const btnSaveRef = useRef<HTMLButtonElement>(null);

  // state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFormReadonly, setIsFormReadonly] = useState<boolean>(false);
  const [storeSettingData, setStoreSettingData] = useState<Restaurant[]>([]);
  const [saleTypeSource, setSaleTypeSource] = useState<SaleType[]>();
  const [counterPatternSource, setCounterPatternSource] = useState<CounterPattern[]>();
  const [organizationData, setOrganizationData] = useState<CompanyNode[] | DepartmentNode[]>();
  const [activeDepartmentIds, setActiveDepartmentIds] = useState<number[]>([]);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    dataDate: new Date(),
    storeName: '',
    saleTypeId: '',
    ngNumberType: 0,
    isConsistencyType: 0,
  });

  // source
  const ngNumberCheckSource = NG_NUMBER_CHECK_SOURCE[i18n.language];
  const consistencyCheckSource = CONSISTENCY_CHECK_SOURCE[i18n.language];

  const saleTypeSourceMap = saleTypeSource?.reduce((saleTypeMap: SaleTypeMap, saleType: SaleType) => {
    saleTypeMap[saleType.id] = saleType.name;
    return saleTypeMap;
  }, {});

  //Store List - Hook Form
  const { register, control, handleSubmit, reset } = useForm<StoreListFormValues>({
    defaultValues: {
      restaurants: [],
    },
    mode: 'onBlur',
  });
  const { fields } = useFieldArray({
    keyName: 'key',
    name: 'restaurants',
    control,
  });

  function loadSaleTypeDataSource() {
    (async () => {
      try {
        const response = await masterDataService.getSaleType();
        setSaleTypeSource(response.data.sale_type);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadCounterPatternSource() {
    (async () => {
      try {
        const response = await masterDataService.getCounterPattern();
        setCounterPatternSource(response.data.counter_pattern);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadStoreSettingData() {
    (async () => {
      try {
        const organizationResponse = await organizationTreeService.getAll({ dataDate: formSearchValue.dataDate });
        setOrganizationData(organizationResponse.data);

        //compose payload
        if (formSearchRef.current) {
          const storeName = (formSearchRef.current['store_name'] as HTMLInputElement).value.trim() as string;
          const saleTypeId = formSearchRef.current['sale_type'].value as string;

          const payload: StoreSettingRequestPayload = {
            data_date: formSearchValue.dataDate,
            name: storeName.length > 0 ? storeName : undefined,
            sale_type_id: saleTypeId.length > 0 ? Number(saleTypeId) : undefined,
            ng_number_type: Number(formSearchRef.current['ng_number'].value),
            is_consistency_type: Number(formSearchRef.current['is_consistency'].value),
          };

          const response = await storeSettingService.getAll(payload);

          // find active departmentids
          let activeDepartmentIds: number[] = findActiveDepartmentIds(organizationResponse.data, response.data);
          const uniqueActiveDepartmentIds = activeDepartmentIds.filter(
            (value, index, array) => array.indexOf(value) === index
          );
          setActiveDepartmentIds(uniqueActiveDepartmentIds);

          setStoreSettingData(response.data);

          reset({
            restaurants: response.data,
          });
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  useEffect(() => {
    if (!isLoading) {
      if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
      if (btnRevalidateRef.current) btnRevalidateRef.current.disabled = true;
      if (btnSaveRef.current) btnSaveRef.current.disabled = true;
    }
  }, [isLoading]);

  useEffect(() => {
    if (saleTypeSource && counterPatternSource) {
      setIsLoading(false);
      loadStoreSettingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleTypeSource, counterPatternSource]);

  useEffect(() => {
    loadSaleTypeDataSource();
    loadCounterPatternSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCloseErrorDataSummaryModal(isDataChanged: boolean) {
    if (isDataChanged) {
      loadStoreSettingData();
    }
  }

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    handleUnSelectAll();
    loadStoreSettingData();

    // if searchDate < today, => enable form Readonly
    const today = new Date();
    const searchDate = formSearchValue.dataDate;
    today.setHours(0, 0, 0, 0);
    searchDate.setHours(0, 0, 0, 0);
    if (searchDate >= today) {
      setIsFormReadonly(false);
    } else if (formSearchValue.dataDate < today) {
      setIsFormReadonly(true);
    }
  }

  function handleCollapsedFormSearch() {
    if (formSearchRef.current) {
      const formValue: FormSearchValueProps = {
        ...formSearchValue,
        storeName: formSearchRef.current['store_name'].value.trim(),
        saleTypeId: formSearchRef.current['sale_type'].value,
        ngNumberType: Number(formSearchRef.current['ng_number'].value),
        isConsistencyType: Number(formSearchRef.current['is_consistency'].value),
      };
      setFormSearchValue(formValue);
    }
  }

  function handleOpenStoreSettingDetail() {
    const chkElements = document.querySelectorAll<HTMLInputElement>(
      '.chk-selected-restaurant .form-check-input:checked'
    );
    if (chkElements && chkElements.length === 1) {
      const chkElement = chkElements[0];
      const restaurantId = chkElement.value;
      const saleTypeId = chkElement.dataset.sale_type_id || '';
      const counterPatternId = chkElement.dataset.counter_pattern_id || '';

      if (saleTypeId.length === 0 || counterPatternId.length === 0) {
        showGlobalModal(() => (
          <AlertModal
            title={t('App.warning')}
            message={t('StoreSettingPage.warn_input_saletype_and_counter_pattern_required')}
          />
        ));
      } else {
        history.push(`/store/store-setting/${restaurantId}`);
      }
    }
  }

  function findActiveDepartmentIdsByDepartment(
    organizationDataSource: CompanyNode[] | DepartmentNode[],
    restaurantSource: Restaurant[],
    department: DepartmentNode
  ) {
    if (department.classification === 'T') {
      const index = restaurantSource.findIndex((restaurantItem) => restaurantItem.id === department.restaurant_id);
      const restaurant = restaurantSource[index];
      if (index < 0 || restaurant === undefined) {
        return [];
      }

      const parentKeys = findParentKeys(organizationDataSource, department.department_id, department.department_cd);
      if (parentKeys) {
        const departmentIds = parentKeys.map((key) => key.id);
        return departmentIds;
      } else {
        return [];
      }
    }

    let activeDepartmentIds: number[] = [];
    department.departments?.forEach(function (departmentChild) {
      const departmentIds = findActiveDepartmentIdsByDepartment(
        organizationDataSource,
        restaurantSource,
        departmentChild
      );
      activeDepartmentIds = activeDepartmentIds.concat(departmentIds);
    });

    return activeDepartmentIds;
  }

  function findActiveDepartmentIds(
    organizationDataSource: CompanyNode[] | DepartmentNode[],
    restaurantSource: Restaurant[]
  ) {
    let activeDepartmentIds: number[] = [];

    organizationDataSource.forEach(function (department) {
      const isCompany = isCompanyNode(department);
      if (!isCompany) {
        const activeDepartmentChildIds = findActiveDepartmentIdsByDepartment(
          organizationDataSource,
          restaurantSource,
          department
        );
        if (activeDepartmentChildIds) {
          activeDepartmentIds = activeDepartmentIds.concat(activeDepartmentChildIds);
        }
      } else {
        department.departments?.forEach(function (departmentChild) {
          const activeDepartmentChildIds = findActiveDepartmentIdsByDepartment(
            organizationDataSource,
            restaurantSource,
            departmentChild
          );
          if (activeDepartmentChildIds) {
            activeDepartmentIds = activeDepartmentIds.concat(activeDepartmentChildIds);
          }
        });
      }
    });

    return activeDepartmentIds;
  }

  function renderRestaurantRow(department: DepartmentNode): JSX.Element | null {
    const index = fields.findIndex((field) => field.id === department.restaurant_id);
    const restaurant = fields[index];
    if (index < 0 || restaurant === undefined) {
      return null;
    }

    let isConsistencyCellHtml: string | JSX.Element = '';
    if (restaurant.is_consistency === null) {
      isConsistencyCellHtml = t('StoreSettingPage.unchecked');
    } else {
      isConsistencyCellHtml = restaurant.is_consistency ? (
        '-'
      ) : (
        <span className="has-issue">{t('StoreSettingPage.can_be')}</span>
      );
    }

    let ngNumberCellHtml: string | JSX.Element = '';
    if (restaurant.ng_number === null) {
      ngNumberCellHtml = t('StoreSettingPage.unchecked');
    } else {
      if (restaurant.ng_number > 0) {
        ngNumberCellHtml = (
          <span className="has-issue">{`${t('StoreSettingPage.ng')} (${restaurant.ng_number}${t(
            'StoreSettingPage.subject'
          )})`}</span>
        );
      } else {
        ngNumberCellHtml = <span>{t('StoreSettingPage.ok')}</span>;
      }
    }

    return (
      <tr key={`restaurant-${restaurant.id}_${restaurant.cd}`}>
        <td>
          <div className="d-flex justify-content-center">
            <Form.Check
              className="chk-selected-restaurant custom-checkbox"
              type="checkbox"
              defaultValue={restaurant.id}
              data-sale_type_id={restaurant.sale_type_id ? restaurant.sale_type_id.toString() : ''}
              data-counter_pattern_id={restaurant.counter_pattern_id ? restaurant.counter_pattern_id.toString() : ''}
              onChange={handleSelectedRowChanged}
            ></Form.Check>
          </div>
        </td>
        <td className="text-center">{restaurant.cd}</td>
        <td className="text-center">{restaurant.name}</td>
        <td className="text-center">
          <Form.Select
            className="text-center"
            {...register(`restaurants.${index}.sale_type_id`, {
              valueAsNumber: true,
            })}
            defaultValue={restaurant.sale_type_id ? restaurant.sale_type_id.toString() : ''}
            disabled={isFormReadonly}
          >
            <option value="" disabled>
              -
            </option>
            {saleTypeSource &&
              saleTypeSource.map((saleType) => (
                <option key={saleType.id} value={saleType.id}>
                  {saleType.name}
                </option>
              ))}
          </Form.Select>
        </td>
        <td className="text-center">
          <Form.Select
            className="text-center"
            {...register(`restaurants.${index}.counter_pattern_id`, {
              valueAsNumber: true,
            })}
            defaultValue={restaurant.counter_pattern_id ? restaurant.counter_pattern_id.toString() : ''}
            disabled={isFormReadonly}
          >
            <option value="" disabled>
              -
            </option>
            {counterPatternSource &&
              counterPatternSource.map((counterPattern) => (
                <option key={counterPattern.id} value={counterPattern.id}>
                  {`${counterPattern.cd} (${counterPattern.cd} ${counterPattern.name})`}
                </option>
              ))}
          </Form.Select>
        </td>
        <td className="text-center">{isConsistencyCellHtml}</td>
        <td className="text-center">{ngNumberCellHtml}</td>
      </tr>
    );
  }

  function renderDepartmentRow(department: CompanyNode | DepartmentNode): JSX.Element | null {
    if (department.classification === 'T') {
      return renderRestaurantRow(department as DepartmentNode);
    }
    const isCompany = isCompanyNode(department);
    const backgroundColor = getBackgroundColorByClassification(department.classification);
    const paddingLeft = isCompany ? 0 : getPaddingByClassification(department);

    if (isCompany) {
      if (!activeDepartmentIds.includes(department.company_id)) return null;
    } else {
      if (!activeDepartmentIds.includes(department.department_id)) return null;
    }

    if (
      department.departments &&
      department.departments.length > 0 &&
      department.departments[0].classification === 'T'
    ) {
      department.departments = _.orderBy(department.departments, ['department_id'], ['asc']);
    }

    return (
      <Fragment
        key={
          isCompany
            ? `company_${department.company_id}-${department.company_cd}`
            : `department_${department.department_id}_${department.department_cd}`
        }
      >
        <tr
          className={`department ${isCompany ? 'd-none' : ''}`}
          style={{ backgroundColor: backgroundColor, color: '#fff' }}
        >
          <td></td>
          <td
            colSpan={2}
            dangerouslySetInnerHTML={{
              __html: paddingLeft + `${isCompany ? department.company_name : department.department_name}`,
            }}
          ></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        {department.departments &&
          department.departments?.map((departmentChild) => renderDepartmentRow(departmentChild))}
      </Fragment>
    );
  }

  const onSave = async (data: StoreListFormValues) => {
    const chkElements = document.querySelectorAll<HTMLInputElement>(
      '.chk-selected-restaurant .form-check-input:checked'
    );
    const selectedRestaurantIds: number[] = [];
    if (chkElements && chkElements.length > 0) {
      chkElements.forEach((element) => {
        selectedRestaurantIds.push(Number(element.value));
      });
    }

    const modifiedData = data.restaurants
      .filter((restaurant) => selectedRestaurantIds.includes(restaurant.id))
      .reduce((restaurants: Restaurant[], restaurant) => {
        return restaurants.concat(
          !storeSettingData.some(
            (oldItem) =>
              oldItem.id === restaurant.id &&
              oldItem.cd === restaurant.cd &&
              oldItem.sale_type_id === restaurant.sale_type_id &&
              oldItem.counter_pattern_id === restaurant.counter_pattern_id
          )
            ? restaurant
            : []
        );
      }, []);

    if (modifiedData.length === 0) {
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('StoreSettingPage.no_data_changed')} />);
    } else {
      // compose payload
      const payload: UpdateStoreSettingRequestPayload[] = modifiedData.map((item) => {
        return {
          id: item.id,
          sale_type_id: item.sale_type_id,
          counter_pattern_id: item.counter_pattern_id,
        };
      });

      try {
        const response = await storeSettingService.update(payload);
        if (Array.isArray(response.data) && response.data.length > 0) {
          showGlobalModal(() => (
            <ErrorDataSummaryModal
              errorFromFeatureId={FEATURE_STORE_ID}
              data={response.data}
              onClose={handleCloseErrorDataSummaryModal}
            />
          ));
        } else {
          showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
        }
        loadStoreSettingData();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    }
  };

  function handleRevalidate() {
    const chkElements = document.querySelectorAll<HTMLInputElement>(
      '.chk-selected-restaurant .form-check-input:checked'
    );
    if (chkElements && chkElements.length > 0) {
      const payload: number[] = [];
      chkElements.forEach((chkElement) => {
        const counterPatternId = chkElement.dataset.counter_pattern_id || '';
        if (counterPatternId !== '') {
          payload.push(Number(chkElement.value));
        }
      });

      (async () => {
        try {
          const response = await storeSettingService.updateNgNumberConsistency(payload);
          if (Array.isArray(response.data) && response.data.length > 0) {
            showGlobalModal(() => (
              <ErrorDataSummaryModal
                errorFromFeatureId={FEATURE_STORE_ID}
                data={response.data}
                onClose={handleCloseErrorDataSummaryModal}
              />
            ));
          } else {
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={t('ErrorDataSummaryModal.not_found_invalid_data')} />
            ));
          }
          loadStoreSettingData();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function handleSelectedRowChanged() {
    if (btnViewDetailRef.current && btnRevalidateRef.current && btnSaveRef.current) {
      const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-selected-restaurant input:checked');
      if (chkSelectionElems.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnRevalidateRef.current.disabled = true;
        btnSaveRef.current.disabled = true;
      } else if (chkSelectionElems.length === 1) {
        btnViewDetailRef.current.disabled = false;
        btnRevalidateRef.current.disabled = false || isFormReadonly;
        btnSaveRef.current.disabled = false || isFormReadonly;
      } else {
        btnViewDetailRef.current.disabled = true;
        btnRevalidateRef.current.disabled = false || isFormReadonly;
        btnSaveRef.current.disabled = false || isFormReadonly;
      }
    }
  }

  function handleSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-selected-restaurant > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = true;
    });

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = chkSelectionElems.length > 1;
    if (btnRevalidateRef.current) btnRevalidateRef.current.disabled = false || isFormReadonly;
    if (btnSaveRef.current) btnSaveRef.current.disabled = false || isFormReadonly;
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-selected-restaurant > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnRevalidateRef.current) btnRevalidateRef.current.disabled = true;
    if (btnSaveRef.current) btnSaveRef.current.disabled = true;
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : undefined}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div className="mb-3">
            <FeatureTitleBar title={t('StoreSettingPage.head_title')}></FeatureTitleBar>
          </div>
          <form ref={formSearchRef} className="mb-3" onSubmit={handleSearchSubmit}>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{t('StoreSettingPage.search')}</Accordion.Header>
                <Accordion.Body onExited={handleCollapsedFormSearch}>
                  <Row className="mb-3">
                    <Col sm={6}>
                      <Row>
                        <Col sm={4} className="text-end">
                          <Form.Label htmlFor="cbo-sale-type" className="col-form-label">
                            {t('StoreSettingPage.sale_type')}：
                          </Form.Label>
                        </Col>
                        <Col sm={4}>
                          <Form.Select name="sale_type" className="text-center">
                            <option value="">{t('StoreSettingPage.all')}</option>
                            {saleTypeSource &&
                              saleTypeSource.map((saleType) => (
                                <option key={saleType.id} value={saleType.id}>
                                  {saleType.name}
                                </option>
                              ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Col>
                    <Col sm={6}>
                      <Row>
                        <Col sm={4} className="text-end">
                          <Form.Label
                            htmlFor="cbo-target-date"
                            className="col-form-label"
                            dangerouslySetInnerHTML={{ __html: `${t('StoreSettingPage.target_date')}：` }}
                          ></Form.Label>
                        </Col>
                        <Col sm={4}>
                          <ReactDatePicker
                            id="cbo-target-date"
                            selected={formSearchValue.dataDate}
                            onChange={(date: Date) => {
                              setFormSearchValue({ ...formSearchValue, dataDate: date });
                            }}
                            customInput={<CustomInputDatePicker readonly />}
                            dateFormat="yyyy/MM/dd"
                            name="data_date"
                            locale="ja"
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm={6}>
                      <Row>
                        <Col sm={4} className="text-end">
                          <Form.Label className="col-form-label">
                            {t('StoreSettingPage.consistency_check')}：
                          </Form.Label>
                        </Col>
                        <Col sm={8}>
                          <div className="col-form-label">
                            {Object.keys(consistencyCheckSource).map(function (key) {
                              return (
                                <Form.Check
                                  key={`is_consistency-${key}`}
                                  inline
                                  label={consistencyCheckSource[key]}
                                  name="is_consistency"
                                  type="radio"
                                  id={`is_consistency-${key}`}
                                  value={key}
                                  defaultChecked={key === '0'}
                                />
                              );
                            })}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={4} className="text-end">
                          <Form.Label className="col-form-label">{t('StoreSettingPage.ng_number_check')}：</Form.Label>
                        </Col>
                        <Col sm={8}>
                          <div className="col-form-label">
                            {Object.keys(ngNumberCheckSource).map(function (key) {
                              return (
                                <Form.Check
                                  key={`ng_number-${key}`}
                                  inline
                                  label={ngNumberCheckSource[key]}
                                  name="ng_number"
                                  type="radio"
                                  id={`ng_number-${key}`}
                                  value={key}
                                  defaultChecked={key === '0'}
                                />
                              );
                            })}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col sm={6}>
                      <Row>
                        <Col sm={4} className="text-end">
                          <Form.Label htmlFor="txt-store-name" className="col-form-label">
                            {t('StoreSettingPage.store_name')}：
                          </Form.Label>
                        </Col>
                        <Col sm={4}>
                          <Form.Control id="txt-store-name" name="store_name" defaultValue="" />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row>
                    <Col className="text-end">
                      <Button type="submit">{t('StoreSettingPage.search')}</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
                <div className="accordion-body accordion-summary">
                  <Row>
                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('StoreSettingPage.target_date')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {moment(formSearchValue.dataDate).format(DATE_FORMAT_YYYY_MM_DD) || '-'}
                      </Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('StoreSettingPage.sale_type')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {(saleTypeSourceMap && saleTypeSourceMap[Number(formSearchValue.saleTypeId)]) ||
                          t('StoreSettingPage.all')}
                      </Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('StoreSettingPage.store_name')}：</Form.Label>
                      <Form.Label className="col-form-label">{formSearchValue.storeName || '-'}</Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('StoreSettingPage.consistency_check')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {consistencyCheckSource[formSearchValue.isConsistencyType]}
                      </Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('StoreSettingPage.ng_number_check')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {ngNumberCheckSource[formSearchValue.ngNumberType]}
                      </Form.Label>
                    </Col>
                  </Row>
                </div>
              </Accordion.Item>
            </Accordion>
          </form>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="d-inline-block me-4">
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleSelectAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('StoreSettingPage.select_all')}
                </Button>
                <span>／</span>
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleUnSelectAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('StoreSettingPage.unselect_all')}
                </Button>
              </div>
            </div>
            <div className="store-setting-list-controls">
              <Button
                ref={btnViewDetailRef}
                style={{ height: '47px', minWidth: '92px' }}
                onClick={handleOpenStoreSettingDetail}
              >
                {t('StoreSettingPage.individual_setting')}
              </Button>

              <Button
                ref={btnRevalidateRef}
                className="ms-3"
                dangerouslySetInnerHTML={{ __html: t('StoreSettingPage.food_registration_leak_check') }}
                onClick={handleRevalidate}
              ></Button>

              <Button
                ref={btnSaveRef}
                className="btn btn--dark-green ms-3"
                style={{ height: '47px', minWidth: '92px' }}
                onClick={handleSubmit(onSave)}
              >
                {t('StoreSettingPage.save')}
              </Button>
            </div>
          </div>

          <div className="sticky-table flex-grow-1 flex-shrink-1">
            <Table hover>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: '50px' }}></th>
                  <th rowSpan={2}>{t('StoreSettingPage.store_code')}</th>
                  <th rowSpan={2}>{t('StoreSettingPage.store_name')}</th>
                  <th rowSpan={2}>{t('StoreSettingPage.sale_type')}</th>
                  <th colSpan={3}>{t('StoreSettingPage.register_pattern')}</th>
                </tr>
                <tr>
                  <th>{t('StoreSettingPage.application_pattern')}</th>
                  <th dangerouslySetInnerHTML={{ __html: t('StoreSettingPage.consistency_html') }}></th>
                  <th
                    dangerouslySetInnerHTML={{
                      __html: t('StoreSettingPage.ingredient_management_registration_omission_check'),
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {organizationData?.map((department) => {
                  currentRestaurantIndex.current = 0;
                  return renderDepartmentRow(department);
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StoreSettingPage;
